import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Download, ChevronLeft, ChevronRight, Loader2, FileText, PlayCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import ProductCard from '../components/ProductCard';
import { gql, useQuery } from '@apollo/client';
import { useState } from 'react';
import { fileUrl, loomEmbedsToHtml, resolveVideoUrl } from '../lib/utils';

// Query for single course (manual/treatment)
const COURSE_DETAIL = gql`
  query Course($courseId: ID!) {
    course(id: $courseId) {
      id
      title
      description
      thumbnail
      videoUrl
      attachment
      duration
      kind
      category
      createdAt
    }
  }
`;


const TreatmentDetail = () => {
  const { id } = useParams<{ id: string }>();

  const { data: courseData, loading: loadingCourse, error: courseError } = useQuery(COURSE_DETAIL, {
    variables: { courseId: id },
    skip: !id,
  });

  const course = courseData?.course;

  // Determine if current item is PRODUCT or DEVICE to show related ones
  const relatedKind = course?.kind === 'PRODUCT' ? 'PRODUCT' : 'DEVICE';

  // const { data: relatedData, loading: loadingRelated } = useQuery(RELATED_PRODUCTS, {
  //   variables: {
  //     kind: 'PRODUCT', // Always show cosmetic products as "related"
  //     limit: 8,
  //   },
  //   skip: !course,
  // });

  // const relatedProducts = relatedData?.courses?.nodes || [];

  if (loadingCourse) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  if (courseError || !course) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg mb-4">Гарын авлага олдсонгүй</p>
          <Link to="/knowledge">
            <Button variant="outline">Бүх гарын авлага руу буцах</Button>
          </Link>
        </div>
      </div>
    );
  }

  const formatDuration = (mins?: number | null) => {
    if (!mins) return 'Тодорхойгүй';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h} цаг ${m} мин` : `${m} минут`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-[#FFA500]/10 py-8">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold text-center text-[#FFA500]">
            {course.title}
          </h1>
        </div>
      </div>


      <div className="container mx-auto px-4 py-12">

       <Link
          to="/knowledge"
          className="inline-flex items-center text-primary hover:underline mb-8 text-lg font-medium"
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
           буцах
        </Link>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Image / Video */}
            <div className="relative">
              {course.videoUrl ? (
                <div className="relative aspect-video rounded-lg overflow-hidden shadow-xl bg-black">
                  <iframe
                    src={resolveVideoUrl(course.videoUrl)}
                    title={course.title}
                    className="w-full h-full"
                    allowFullScreen
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  />
                  <div className="absolute bottom-4 left-4 flex items-center gap-2 bg-black/70 text-white px-3 py-1 rounded">
                    <PlayCircle className="h-5 w-5" />
                    <span className="text-sm">Видео заавар</span>
                  </div>
                </div>
              ) : course.thumbnail ? (
                <img
                  src={fileUrl(course.thumbnail)}
                  alt={course.title}
                  className="w-full rounded-lg shadow-lg object-cover"
                />
              ) : (
                <div className="bg-muted border-2 border-dashed rounded-xl w-full h-96 flex items-center justify-center">
                  <span className="text-muted-foreground">Зураг/Видео байхгүй</span>
                </div>
              )}
            </div>

            {/* Content */}
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-primary">
                {course.kind === 'PRODUCT' ? 'Бүтээгдэхүүний гарын авлага' : 'Багаж хэрэгсэл ашиглах заавар'}
              </h2>

              <p className="text-muted-foreground mb-6 leading-relaxed text-lg" dangerouslySetInnerHTML={{ __html: loomEmbedsToHtml(course.description || 'Тодорхойлолт байхгүй байна.') }} />

              <div className="space-y-5 mb-8">
                {course.duration && (
                  <p className="text-sm">
                    <span className="font-semibold">Үргэлжлэх хугацаа:</span>{' '}
                    <span className="text-muted-foreground">
                      ойролцоогоор {formatDuration(parseInt(course.duration))}
                    </span>
                  </p>
                )}

                <div className="flex gap-4">
                  {course.attachment && (
                    <a
                      href={fileUrl(course.attachment)}
                      target="_blank"
                      rel="noopener noreferrer"
                      download
                    >
                      <Button className="bg-primary hover:bg-primary/90">
                        <Download className="h-4 w-4 mr-2" />
                        PDF татах
                      </Button>
                    </a>
                  )}

                  {/* {course.videoUrl && (
                    <Button variant="outline" asChild>
                      <a href={fileUrl(course.videoUrl)} target="_blank" rel="noopener noreferrer">
                        <PlayCircle className="h-4 w-4 mr-2" />
                        Видео үзэх
                      </a>
                    </Button>
                  )} */}
                </div>
              </div>

        
            </div>
          </div>

          {/* Related Products Section */}
          {/* <div className="mt-20">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#FFA500] mb-2">
                ХОЛБООТОЙ БҮТЭЭГДЭХҮҮНҮҮД
              </h2>
              <div className="w-24 h-1 bg-border mx-auto"></div>
            </div>

            {loadingRelated ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : relatedProducts.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">
                Холбоотой бүтээгдэхүүн байхгүй байна.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {relatedProducts.map((product: any) => (
                  <ProductCard
                    key={product.id}
                    id={product.id}
                    name={product.title}
                    description={product.description || ''}
                    price={product.price || 0}
                    image={product.thumbnail || ''}
                    isNew={false}
                  />
                ))}
              </div>
            )}
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default TreatmentDetail;